import { ProgressivePIT } from './types';

export const pct = (value: number) => value / 100;

export const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export function monthlyRateFromAnnual(annualPct: number): number {
  const annual = pct(annualPct);
  return Math.pow(1 + annual, 1 / 12) - 1;
}

export function realAnnualReturn(nominalReturnPct: number, inflationPct: number): number {
  const nominal = pct(nominalReturnPct);
  const inflation = pct(inflationPct);
  return (1 + nominal) / (1 + inflation) - 1;
}

export function computeProgressivePIT(gross: number, progressive: ProgressivePIT) {
  const { threshold, lowerRatePct, upperRatePct, healthPct } = progressive;
  const lowerRate = pct(lowerRatePct);
  const upperRate = pct(upperRatePct);
  const healthRate = pct(healthPct);

  const monthlyThreshold = threshold / 12;
  const lowerTaxable = Math.min(gross, monthlyThreshold);
  const upperTaxable = Math.max(gross - monthlyThreshold, 0);

  const pit = lowerTaxable * lowerRate + upperTaxable * upperRate;
  const health = gross * healthRate;
  const net = gross - pit - health;

  return { net, pit, health };
}

export function solveGrossForNet(targetNet: number, progressive: ProgressivePIT, minGross: number) {
  if (targetNet <= 0) {
    return { gross: 0, pit: 0, health: 0, net: 0 };
  }

  let low = minGross;
  let high = Math.max(minGross, targetNet * 3);

  for (let i = 0; i < 40; i++) {
    const mid = (low + high) / 2;
    const { net } = computeProgressivePIT(mid, progressive);
    if (net < targetNet) {
      low = mid;
    } else {
      high = mid;
    }
  }

  const gross = high;
  const { pit, health, net } = computeProgressivePIT(gross, progressive);
  return { gross, pit, health, net };
}

export function sum(values: number[]) {
  return values.reduce((a, b) => a + b, 0);
}

export function formatCurrency(value: number, currency = 'PLN') {
  return new Intl.NumberFormat('pl-PL', { style: 'currency', currency }).format(value);
}
